/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import Layout from '../../Components/Core/Layout';
import SEO from '../../Components/Core/Seo';
import BackToTop from '../../Components/Core/BackToTop';
import PageBuilder from '../../Components/Core/PageBuilder';
import PCMoneyCDIC from '../../Components/Screens/CDIC/PCMoneyCDIC';
import {
  heroBanner,
  simpleBanner,
  contactTable,
  statementSection,
  titleOrImageWithIconLists,
  legal,
  faqBlock,
  appBanner,
  blogCards,
  optimumStoresBanner,
  faqPopularQuestion,
  containerSection,
  proTip,
  chart,
  optimumPointRoller,
  calculator,
  productDetails,
  gallery,
  paragraph,
  searchSection,
  faqSection,
  mediaCentre,
  legalArticle,
  // stepper,
  videos,
} from '../../utils/variation';

const pageTemplate = {
  ...heroBanner,
  ...contactTable,
  ...statementSection,
  ...simpleBanner,
  ...chart,
  ...calculator,
  'cdic-banner': PCMoneyCDIC,
  ...titleOrImageWithIconLists,
  ...faqBlock,
  ...legal,
  ...appBanner,
  ...blogCards,
  ...optimumStoresBanner,
  ...optimumPointRoller,
  ...faqPopularQuestion,
  ...containerSection,
  ...proTip,
  ...productDetails,
  ...gallery,
  ...paragraph,
  ...searchSection,
  ...faqSection,
  ...mediaCentre,
  ...legalArticle,
  // ...stepper,
  ...videos,
};

const PageTemplate = ({ location, data }) => {
  const {
    contentfulPageLayout: {
      languageToggle,
      redirectPromoCode,
      promoCode,
      topNotificationBanner,
      customHeader,
      sections,
      customFooter,
      internal: { type },
      pageSlug,
    },
  } = data;

  return (
    <Layout
      location={location}
      header={customHeader}
      footer={customFooter}
      languageToggle={languageToggle}
      promoCode={promoCode}
      redirectPromoCode={redirectPromoCode}
      banner={topNotificationBanner}
      contentType={type}
    >
      <PageBuilder
        sections={sections}
        template={pageTemplate}
        pageSlug={pageSlug}
      />
      <BackToTop />
    </Layout>
  );
};

PageTemplate.propTypes = {
  data: PropTypes.shape().isRequired,
  location: PropTypes.shape().isRequired,
};

export default PageTemplate;

export const query = graphql`
  query pageTemplate($locale: String = "en-CA", $contentful_id: String!) {
    contentfulPageLayout(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      node_locale
      pageTitle
      pageSlug
      metaDescription
      languageToggle
      displayInSiteMap
      internal {
        type
      }
      promoCode
      redirectPromoCode {
        redirectedPromoCode
        redirectedUrl
      }
      ...global_header
      ...notification_banners
      sections {
        ...app_banner
        ...app_banner_stepper
        ...blog_articles
        ...calculator
        ...titleOrImageWithIconLists
        ...faq_popular_question
        ...faq_search_section
        ...gallery
        ...hero_banner
        ...legal_article_section
        ...legal_details
        ...optimum_stores_banner
        ...paragraph
        ...pro_tip
        ...product_chart
        ...section_container
        ...simple_image_banner
        ...statement_section
        ...table
        ...ticker_section
        ...video_item
      }
      ...global_footer
    }
  }
`;

export const Head = ({ location, data, pageContext }) => {
  const { pageTitle, pageSlug, metaDescription, sections } =
    data.contentfulPageLayout;
  let heroImage = '';
  let schema;
  const schemaSwap = pageUrl => {
    switch (pageUrl) {
      case 'learning-hub/faqs':
        schema = {
          '@context': 'https://schema.org/',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `https://www.pcfinancial.ca/${pageContext.language}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Learning Hub',
              item: `https://www.pcfinancial.ca/${pageContext.language}/learning-hub/`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: pageTitle,
              item: `https://www.pcfinancial.ca${location.pathname}`,
            },
          ],
        };
        break;
      case 'pc-money-account':
        if (sections[0].internal.type === 'ContentfulHeroBanner') {
          heroImage = sections[0].mediaimageDam[0].asset.localImage.publicURL;
        }
        schema = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '',
            reviewCount: '',
          },
          description: metaDescription,
          name:
            pageContext.language === 'en'
              ? 'PC Money Account'
              : 'Le compe PC Argent',
          image: heroImage,
          offers: {
            '@type': 'Offer',
            availability: 'https://schema.org/InStock',
            price: '0.00',
            priceCurrency: 'CAD',
          },
          '@graph': [
            {
              '@type': 'BankAccount',
              name:
                pageContext.language === 'en'
                  ? 'PC Money Account'
                  : 'Le compe PC Argent',
              offers: {
                '@type': 'Offer',
                eligibleCustomerType: {
                  '@id': '',
                },
                price: '0',
                description: metaDescription,
                offeredBy: {
                  '@type': 'BankOrCreditUnion',
                  '@id': `https://www.pcfinancial.ca/${pageContext.language}/`,
                  name:
                    pageContext.language === 'en'
                      ? 'PC Financial'
                      : 'PC Finance',
                },
              },
              feesAndCommissionsSpecification: `https://www.pcfinancial.ca/${pageContext.language}/legal-stuff/`,
            },
          ],
        };
        break;
      default:
        schema = {
          '@context': 'https://schema.org/',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `https://www.pcfinancial.ca/${pageContext.language}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: pageTitle,
              item: `https://www.pcfinancial.ca${location.pathname}`,
            },
          ],
        };
        break;
    }
  };

  if (location) schemaSwap(pageSlug);

  return (
    <SEO
      data={data.contentfulPageLayout}
      pageContext={pageContext}
      location={location}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </SEO>
  );
};
